import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { canApproveMcp, hasRole } from "@lawline/shared/access/platform";
import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@lawline/ui/components/dialog";
import { Label } from "@lawline/ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@lawline/ui/components/select";

import { useAuth } from "@platform/auth/context";
import { changeMemberRoleMutation } from "../tenant.mutations";
import { membersQueryOptions } from "../tenant.queries";
import type { Member } from "../tenant.types";

const ROLES: Member["role"][] = ["member", "admin", "owner"];

export function EditMemberDialog({
	member,
	open,
	onOpenChange,
}: {
	member: Member;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const t = useTranslations("tenant.editMember");
	const { user, role: actorRole } = useAuth();
	const { data: members } = useQuery(membersQueryOptions);
	const change = useMutation(changeMemberRoleMutation);
	const [role, setRole] = useState<Member["role"]>(member.role);

	const roleOptions = hasRole(actorRole ?? "", "owner")
		? ROLES
		: ROLES.filter((r) => r !== "owner");
	const selfMemberId = members?.find((m) => m.userId === user?.id)?.id ?? null;
	const transferring = role === "owner" && !hasRole(member.role, "owner");
	// An app connection works only while the person who approved it holds a role that may approve one.
	const disconnectsApps = canApproveMcp(member.role) && !canApproveMcp(role);
	const dirty = role !== member.role;

	function save() {
		change.mutate(
			{ memberId: member.id, role, stepDownMemberId: transferring ? selfMemberId : null },
			{ onSuccess: () => onOpenChange(false) },
		);
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (next) {
					setRole(member.role);
					change.reset();
				}
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{member.email}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-2">
					<Label htmlFor="edit-role">{t("roleLabel")}</Label>
					<Select value={role} onValueChange={(v) => setRole(v as Member["role"])}>
						<SelectTrigger id="edit-role" className="capitalize">
							<SelectValue />
						</SelectTrigger>
						<SelectContent position="popper">
							{roleOptions.map((r) => (
								<SelectItem key={r} value={r} className="capitalize">
									{r}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{transferring && (
					<p className="text-sm text-amber-600 dark:text-amber-400">
						{t("transferOwnershipWarning", { name: member.name })}
					</p>
				)}

				{disconnectsApps && (
					<p className="text-sm text-amber-600 dark:text-amber-400">{t("disconnectAppsWarning")}</p>
				)}

				{change.error && <p className="text-destructive text-sm">{change.error.message}</p>}

				<DialogFooter>
					<Button onClick={save} disabled={!dirty || change.isPending}>
						{change.isPending ? t("saving") : transferring ? t("transferSubmit") : t("submit")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
